import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import PrintIcon from '@material-ui/icons/Print';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import { CSVLink } from 'react-csv';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { gql, useQuery } from '@apollo/client';
import { formatDateOnly, formatTimeOnly, getDateInFilenameFormat } from '../util/dateHelpers';
import CardholderActivityReport from './PdfExports/CardholderActivityReportPdf.jsx';
import ReportsCardholderActivityList from './ReportsCardholderActivityList.jsx';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';

const sleep = (milliseconds) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};

const useStyles = makeStyles((theme) => ({
  buttonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.header.main,
  },
  reportWrapper: {
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  reportColumn: {
    maxWidth: '50%',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '100%',
    },
  },
  reportColumnRight: {
    width: '100%',
    maxHeight: '35vh',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '100%',
    },
    overflowY: 'auto',
  },
  exportButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  buttonLink: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
  },
  option: {
    textAlign: 'center',
  },
  blueIcon: {
    color: theme.palette.secondary.main,
  },
  centerButton: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  buttonIcon: {
    marginLeft: theme.spacing(1),
  },
}));

const FETCHCARDHOLDERREPORTS = gql`
  query cardHolderActivity($cardholderId: Int!) {
    cardHolderActivity(cardholderId: $cardholderId) {
      cardHolderActivity {
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
        transTime
      }
      last30Days {
        tranCode
        transactionId
        transTime
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
      }
      last14Days {
        tranCode
        transactionId
        transTime
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
      }
      last7Days {
        tranCode
        transactionId
        transTime
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
      }
      last24Hrs {
        tranCode
        transactionId
        transTime
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
      }
      last48Hrs {
        tranCode
        transactionId
        transTime
        cardHolderId
        cardHolderName
        cardNumber
        changeDetail
        description
      }
    }
  }
`;

const ReportsCardholderActivity = (props) => {
  const { data, logo, timeSelector, id, updateTimeSelector, cardholderName } = props;
  const [reportOpen, setReportOpen] = useState(false);
  const [snackbarMsg, setsnackbarMsg] = useState('Quick Report Loaded!');
  const queryError = ({ graphQLErrors, networkError }) => {};

  const [cardHoldersReportData, setCardHoldersReportData] = React.useState({});

  const queryCardHoldersReportCompleted = (data) => {
    setCardHoldersReportData({ ...data });
  };

  const { loading: loadingMrdDoors, ERROR: errorMrdDoors } = useQuery(FETCHCARDHOLDERREPORTS, {
    variables: { cardholderId: id },
    notifyOnNetworkStatusChange: true,
    onCompleted: queryCardHoldersReportCompleted,
    onError: queryError,
    fetchPolicy: 'no-cache',
  });

  const classes = useStyles();
  const [showExportModal, setShowExportModal] = React.useState(false);
  // const csvData = [['name', 'credential', 'timestamp', 'type']];
  const csvData = [['name', 'date', 'time', 'door name: action', 'card number']];

  const handleClose = () => {
    setShowExportModal(false);
  };

  // generate data for CSV download
  if (Object.keys(cardHoldersReportData).length > 0) {
    cardHoldersReportData.cardHolderActivity[`${timeSelector}`].forEach((item) => {
      csvData.push([
        cardholderName,
        formatDateOnly(item.transTime),
        formatTimeOnly(item.transTime),
        item.description,
        item.cardNumber,
      ]);
    });
  }

  const handlecheckActivity = () => {
    if (Object.keys(cardHoldersReportData).length > 0) {
      if (cardHoldersReportData.cardHolderActivity[`${timeSelector}`].length > 0) {
        setsnackbarMsg('Quick Report Loaded!');
        return true;
      } else {
        setsnackbarMsg('No Activity for Report!');
        return false;
      }
    }
  };

  const activityList = (
    <ReportsCardholderActivityList
      data={
        Object.keys(cardHoldersReportData).length > 0
          ? cardHoldersReportData.cardHolderActivity[`${timeSelector}`]
          : []
      }
    />
  );

  const handleTimeFrameChange = (event) => {
    updateTimeSelector(event.target.value);
  };

  const handleReportSnackClose = () => {
    setReportOpen(false);
  };
  const handleReportSnackOpen = () => {
    setReportOpen(true);
  };

  const reportAction = (
    <React.Fragment>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleReportSnackClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <div className={classes.reportWrapper} id="cardholderActivityReport">
      <div className={classes.reportColumn}>
        <Typography variant="h6">
          Cardholder Activity Report <InfoOutlinedIcon className={classes.blueIcon} />
        </Typography>

        <Typography>
          Select a time frame or select custom to be taken to the advanced reporting configuration
          to run a custom time range report
        </Typography>

        <div className={classes.verticalFlex}>
          <div className={classes.centerButton}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="demo-simple-select-outlined-label">Time Frame</InputLabel>
              <Select
                labelId="demo-simple-select-outlined-label"
                id="demo-simple-select-outlined"
                value={timeSelector}
                onChange={handleTimeFrameChange}
                label="Time Frame"
              >
                <MenuItem value="last24Hrs">Last 24 Hour(s)</MenuItem>
                <MenuItem value="last48Hrs">Last 48 Hour(s)</MenuItem>
                <MenuItem value="last7Days">Last Week</MenuItem>
                <MenuItem value="last14Days">Last 2 Week(s)</MenuItem>
                <MenuItem value="last30Days">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className={classes.centerButton}>
            <Button
              variant="contained"
              className={classes.exportButton}
              onClick={() => {
                var activity = handlecheckActivity();
                sleep(500).then(() => {
                  if (activity) {
                    setShowExportModal(true);
                  } else {
                    handleReportSnackOpen();
                  }
                });
              }}
            >
              Export Report <PrintIcon className={classes.buttonIcon} />
            </Button>
          </div>
        </div>
      </div>
      <div className={classes.reportColumnRight}>{activityList}</div>
      <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={showExportModal}>
        <DialogTitle id="simple-dialog-title">Export Cardholder Activity Report</DialogTitle>
        <List>
          <PDFDownloadLink
            className={classes.buttonLink}
            document={
              <CardholderActivityReport
                data={
                  Object.keys(cardHoldersReportData).length > 0
                    ? cardHoldersReportData.cardHolderActivity[`${timeSelector}`]
                    : []
                }
                logo={logo}
              />
            }
            fileName={`CHAR-${cardholderName}-${timeSelector}-${getDateInFilenameFormat()}.pdf`}
          >
            {() => (
              <ListItem button>
                <ListItemText className={classes.option} onClick={handleReportSnackOpen}>
                  Export To PDF
                </ListItemText>
              </ListItem>
            )}
          </PDFDownloadLink>
          <CSVLink
            className={classes.buttonLink}
            data={csvData}
            filename={`CHAR-${cardholderName}-${timeSelector}-${getDateInFilenameFormat()}.csv`}
          >
            <ListItem button>
              <ListItemText className={classes.option} onClick={handleReportSnackOpen}>
                Export To CSV
              </ListItemText>
            </ListItem>
          </CSVLink>
        </List>
      </Dialog>
      <Snackbar
        open={reportOpen}
        autoHideDuration={6000}
        onClose={handleReportSnackClose}
        message={snackbarMsg}
        action={reportAction}
      />
    </div>
  );
};

export default ReportsCardholderActivity;
